import React, { useState } from 'react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, DollarSignIcon, CalendarIcon, BuildingIcon, UserIcon, FilterIcon, DownloadIcon } from 'lucide-react';
export function InvoicePaymentStatusReport({
  filters,
  locale = 'en-US'
}) {
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  // Sample data for the report
  const invoices = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-45678',
    vendor: 'Office Depot',
    vendorId: 'vendor-001',
    amount: 1250.75,
    currency: 'USD',
    issueDate: '2023-10-05',
    dueDate: '2023-11-04',
    paymentDate: '2023-10-30',
    status: 'Paid',
    paymentMethod: 'Bank Transfer',
    paymentReference: 'REF-BT-001',
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'IT Department',
    approvedBy: 'John Smith',
    notes: 'Office supplies for Q4'
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-98765',
    vendor: 'Dell Technologies',
    vendorId: 'vendor-002',
    amount: 5430.0,
    currency: 'USD',
    issueDate: '2023-10-10',
    dueDate: '2023-11-09',
    paymentDate: null,
    status: 'Pending',
    paymentMethod: null,
    paymentReference: null,
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'IT Department',
    approvedBy: 'Jane Doe',
    notes: 'New laptops for engineering team'
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-12345',
    vendor: 'PrintWorks Inc.',
    vendorId: 'vendor-003',
    amount: 3200.5,
    currency: 'USD',
    issueDate: '2023-09-15',
    dueDate: '2023-10-15',
    paymentDate: null,
    status: 'Overdue',
    paymentMethod: null,
    paymentReference: null,
    entity: 'European Operations Ltd',
    entityId: 'ent-002',
    department: 'Marketing',
    approvedBy: 'Alice Johnson',
    notes: 'Marketing materials for trade show'
  }, {
    id: 'INV-2023-004',
    invoiceNumber: 'INV-67890',
    vendor: 'Server Solutions Inc.',
    vendorId: 'vendor-004',
    amount: 12500.0,
    currency: 'USD',
    issueDate: '2023-10-20',
    dueDate: '2023-11-19',
    paymentDate: null,
    status: 'Pending',
    paymentMethod: null,
    paymentReference: null,
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'IT Department',
    approvedBy: 'John Smith',
    notes: 'Server hardware upgrade'
  }, {
    id: 'INV-2023-005',
    invoiceNumber: 'INV-54321',
    vendor: 'Office Depot',
    vendorId: 'vendor-001',
    amount: 750.25,
    currency: 'USD',
    issueDate: '2023-09-01',
    dueDate: '2023-10-01',
    paymentDate: '2023-09-25',
    status: 'Paid',
    paymentMethod: 'Credit Card',
    paymentReference: 'REF-CC-001',
    entity: 'Asia Pacific Division',
    entityId: 'ent-003',
    department: 'Operations',
    approvedBy: 'Bob Wilson',
    notes: 'Office supplies for Singapore office'
  }, {
    id: 'INV-2023-006',
    invoiceNumber: 'INV-13579',
    vendor: 'Tech Solutions Inc.',
    vendorId: 'vendor-005',
    amount: 8750.0,
    currency: 'EUR',
    issueDate: '2023-09-10',
    dueDate: '2023-10-10',
    paymentDate: null,
    status: 'Overdue',
    paymentMethod: null,
    paymentReference: null,
    entity: 'European Operations Ltd',
    entityId: 'ent-002',
    department: 'IT Department',
    approvedBy: 'Claire Martin',
    notes: 'IT consulting services'
  }, {
    id: 'INV-2023-007',
    invoiceNumber: 'INV-24680',
    vendor: 'Global Shipping Co.',
    vendorId: 'vendor-006',
    amount: 1875.5,
    currency: 'USD',
    issueDate: '2023-10-25',
    dueDate: '2023-11-24',
    paymentDate: null,
    status: 'Pending',
    paymentMethod: null,
    paymentReference: null,
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'Logistics',
    approvedBy: 'David Brown',
    notes: 'Shipping fees for Q4 product deliveries'
  }, {
    id: 'INV-2023-008',
    invoiceNumber: 'INV-97531',
    vendor: 'Cleaning Services Ltd',
    vendorId: 'vendor-007',
    amount: 950.0,
    currency: 'GBP',
    issueDate: '2023-10-01',
    dueDate: '2023-10-31',
    paymentDate: '2023-10-28',
    status: 'Paid',
    paymentMethod: 'Bank Transfer',
    paymentReference: 'REF-BT-002',
    entity: 'European Operations Ltd',
    entityId: 'ent-002',
    department: 'Facilities',
    approvedBy: 'Emma Scott',
    notes: 'Monthly cleaning services for London office'
  }];
  // Apply filters based on the filters prop
  const filteredInvoices = invoices.filter(invoice => {
    // Filter by entity if specified
    if (filters.entity && invoice.entityId !== filters.entity) {
      return false;
    }
    // Filter by vendor if specified
    if (filters.vendor && invoice.vendorId !== filters.vendor) {
      return false;
    }
    // Filter by status if specified
    if (filters.status) {
      const statusLower = filters.status.toLowerCase();
      const invoiceStatusLower = invoice.status.toLowerCase();
      if (statusLower === 'pending-approval' && invoiceStatusLower !== 'pending') {
        return false;
      } else if (statusLower !== 'pending-approval' && statusLower !== invoiceStatusLower) {
        return false;
      }
    }
    // Filter by date range
    if (filters.startDate && filters.endDate) {
      const invoiceDate = new Date(invoice.issueDate);
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      if (invoiceDate < startDate || invoiceDate > endDate) {
        return false;
      }
    }
    return true;
  });
  // Sort the filtered invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    if (sortField === 'amount') {
      return sortDirection === 'asc' ? a.amount - b.amount : b.amount - a.amount;
    } else if (sortField === 'dueDate') {
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortField === 'issueDate') {
      const dateA = new Date(a.issueDate);
      const dateB = new Date(b.issueDate);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortField === 'vendor') {
      return sortDirection === 'asc' ? a.vendor.localeCompare(b.vendor) : b.vendor.localeCompare(a.vendor);
    } else if (sortField === 'status') {
      return sortDirection === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
    }
    return 0;
  });
  // Calculate summary statistics
  const totalAmount = filteredInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const paidInvoices = filteredInvoices.filter(invoice => invoice.status === 'Paid');
  const paidAmount = paidInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const pendingInvoices = filteredInvoices.filter(invoice => invoice.status === 'Pending');
  const pendingAmount = pendingInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const overdueInvoices = filteredInvoices.filter(invoice => invoice.status === 'Overdue');
  const overdueAmount = overdueInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Function to render the sort arrow
  const renderSortArrow = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4" data-id="element-14233" /> : <ArrowDownIcon className="h-4 w-4" data-id="element-14234" />;
  };
  // Function to render the status badge
  const renderStatusBadge = status => {
    switch (status) {
      case 'Paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-14235">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-14236" />
            Paid
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-14237">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-14238" />
            Pending
          </span>;
      case 'Overdue':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-14239">
            <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-14240" />
            Overdue
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-14241">
            {status}
          </span>;
    }
  };
  return <div className="p-6" data-id="element-14242">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6" data-id="element-14243">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14244">
          <div className="p-5" data-id="element-14245">
            <div className="flex items-center" data-id="element-14246">
              <div className="flex-shrink-0" data-id="element-14247">
                <DollarSignIcon className="h-6 w-6 text-gray-400" data-id="element-14248" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14249">
                <dl data-id="element-14250">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14251">
                    Total Invoices
                  </dt>
                  <dd data-id="element-14252">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14253">
                      {formatCurrency(totalAmount, 'USD', locale)} (
                      {filteredInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14254">
          <div className="p-5" data-id="element-14255">
            <div className="flex items-center" data-id="element-14256">
              <div className="flex-shrink-0" data-id="element-14257">
                <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-14258" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14259">
                <dl data-id="element-14260">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14261">
                    Paid Invoices
                  </dt>
                  <dd data-id="element-14262">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14263">
                      {formatCurrency(paidAmount, 'USD', locale)} (
                      {paidInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14264">
          <div className="p-5" data-id="element-14265">
            <div className="flex items-center" data-id="element-14266">
              <div className="flex-shrink-0" data-id="element-14267">
                <ClockIcon className="h-6 w-6 text-blue-500" data-id="element-14268" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14269">
                <dl data-id="element-14270">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14271">
                    Pending Invoices
                  </dt>
                  <dd data-id="element-14272">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14273">
                      {formatCurrency(pendingAmount, 'USD', locale)} (
                      {pendingInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14274">
          <div className="p-5" data-id="element-14275">
            <div className="flex items-center" data-id="element-14276">
              <div className="flex-shrink-0" data-id="element-14277">
                <AlertCircleIcon className="h-6 w-6 text-red-500" data-id="element-14278" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14279">
                <dl data-id="element-14280">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14281">
                    Overdue Invoices
                  </dt>
                  <dd data-id="element-14282">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14283">
                      {formatCurrency(overdueAmount, 'USD', locale)} (
                      {overdueInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoices table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden" data-id="element-14284">
        <div className="overflow-x-auto" data-id="element-14285">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-14286">
            <thead className="bg-gray-50" data-id="element-14287">
              <tr data-id="element-14288">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('vendor')} data-id="element-14289">
                  <div className="flex items-center" data-id="element-14290">
                    <span data-id="element-14291">Vendor</span>
                    {renderSortArrow('vendor')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('invoiceNumber')} data-id="element-14292">
                  <div className="flex items-center" data-id="element-14293">
                    <span data-id="element-14294">Invoice #</span>
                    {renderSortArrow('invoiceNumber')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-14295">
                  <div className="flex items-center" data-id="element-14296">
                    <span data-id="element-14297">Amount</span>
                    {renderSortArrow('amount')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('issueDate')} data-id="element-14298">
                  <div className="flex items-center" data-id="element-14299">
                    <span data-id="element-14300">Issue Date</span>
                    {renderSortArrow('issueDate')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('dueDate')} data-id="element-14301">
                  <div className="flex items-center" data-id="element-14302">
                    <span data-id="element-14303">Due Date</span>
                    {renderSortArrow('dueDate')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-14304">
                  <div className="flex items-center" data-id="element-14305">
                    <span data-id="element-14306">Status</span>
                    {renderSortArrow('status')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14307">
                  Entity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14308">
                  Payment Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-14309">
              {sortedInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-14310">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-14311">
                    {invoice.vendor}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14312">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14313">
                    {formatCurrency(invoice.amount, invoice.currency, locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14314">
                    {new Date(invoice.issueDate).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14315">
                    {new Date(invoice.dueDate).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14316">
                    {renderStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14317">
                    {invoice.entity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14318">
                    {invoice.status === 'Paid' ? <div data-id="element-14319">
                        <div data-id="element-14320">{invoice.paymentMethod}</div>
                        <div className="text-xs text-gray-400" data-id="element-14321">
                          {invoice.paymentReference}
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-14322">
                          Paid on{' '}
                          {new Date(invoice.paymentDate).toLocaleDateString(locale)}
                        </div>
                      </div> : <span className="text-xs italic text-gray-400" data-id="element-14323">
                        {invoice.status === 'Overdue' ? `Overdue by ${Math.floor((new Date() - new Date(invoice.dueDate)) / (1000 * 60 * 60 * 24))} days` : 'Not yet paid'}
                      </span>}
                  </td>
                </tr>)}
              {sortedInvoices.length === 0 && <tr data-id="element-14324">
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-14325">
                    No invoices found matching the current filters
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Legend */}
      <div className="mt-6 bg-white shadow-sm rounded-lg p-4" data-id="element-14326">
        <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-14327">
          Status Legend
        </h3>
        <div className="flex flex-wrap gap-4" data-id="element-14328">
          <div className="flex items-center" data-id="element-14329">
            <span className="inline-block h-3 w-3 rounded-full bg-green-500 mr-2" data-id="element-14330"></span>
            <span className="text-sm text-gray-600" data-id="element-14331">
              Paid: Invoice has been paid in full
            </span>
          </div>
          <div className="flex items-center" data-id="element-14332">
            <span className="inline-block h-3 w-3 rounded-full bg-blue-500 mr-2" data-id="element-14333"></span>
            <span className="text-sm text-gray-600" data-id="element-14334">
              Pending: Invoice awaiting payment
            </span>
          </div>
          <div className="flex items-center" data-id="element-14335">
            <span className="inline-block h-3 w-3 rounded-full bg-red-500 mr-2" data-id="element-14336"></span>
            <span className="text-sm text-gray-600" data-id="element-14337">
              Overdue: Payment deadline has passed
            </span>
          </div>
        </div>
      </div>
    </div>;
}